import { Check, Copy, Download, ExternalLink, QrCode } from "lucide-react";
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { formatCurrency } from "@/domain/billing";
import type { InvoiceRecord, PaymentQrMethod } from "@/domain/types";
import { enabledPaymentQrMethods, preferredEnabledPaymentQrMethod } from "@/features/invoices/payment-qr";

interface Props {
  invoice: InvoiceRecord;
}

export function PaymentQrPanel({ invoice }: Props) {
  const enabledMethods = useMemo(
    () => enabledPaymentQrMethods(invoice.paymentQr, invoice.paymentQrAssets),
    [invoice.paymentQr, invoice.paymentQrAssets],
  );
  const initialMethod = preferredEnabledPaymentQrMethod(invoice.paymentQr, invoice.paymentQrAssets);
  const [selected, setSelected] = useState<PaymentQrMethod | null>(initialMethod);
  const [copied, setCopied] = useState(false);

  const activeMethod = selected && enabledMethods.includes(selected)
    ? selected
    : enabledMethods[0] ?? null;
  const usesPhpSettlement = invoice.currency !== "PHP";

  if (invoice.totals.balanceCents <= 0 || !activeMethod) return null;

  const asset = invoice.paymentQrAssets[activeMethod];
  if (!asset.imageUrl) return null;
  const downloadName = `${invoice.invoiceNumber}-${activeMethod}-qr.png`;

  const copyReference = async () => {
    await navigator.clipboard.writeText(invoice.invoiceNumber);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border bg-ink px-5 py-5 text-white sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-brand-300">
            <QrCode className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-black">Pay this invoice</h2>
            <p className="mt-1 text-sm leading-6 text-white/65">Scan using the selected wallet or supported banking app.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_330px]">
        <div className="p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-surface-muted px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">{usesPhpSettlement ? "Invoice balance" : "Amount to send"}</p>
              <p className="mt-1 text-xl font-black text-ink">{formatCurrency(invoice.totals.balanceCents, invoice.currency)}</p>
            </div>
            <div className="rounded-xl bg-surface-muted px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">Payment reference</p>
              <div className="mt-1 flex items-center justify-between gap-3">
                <p className="font-black text-ink">{invoice.invoiceNumber}</p>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-muted hover:bg-white hover:text-ink"
                  aria-label="Copy invoice payment reference"
                  onClick={() => void copyReference()}
                >
                  {copied ? <Check className="h-4 w-4 text-brand-700" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {usesPhpSettlement ? (
            <div className="mt-4 rounded-xl border border-gold-100 bg-gold-50 px-4 py-3 text-xs leading-5 text-gold-800">
              This invoice is billed in {invoice.currency}, but this QR receives PHP. Pay only the converted PHP amount agreed with the issuer; the balance shown above remains in the invoice currency.
            </div>
          ) : null}

          {enabledMethods.length > 1 ? (
            <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Payment QR method">
              {enabledMethods.map((method) => {
                const methodAsset = invoice.paymentQrAssets[method];
                const active = method === activeMethod;
                return (
                  <button
                    key={method}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`min-h-10 rounded-xl px-4 text-sm font-bold transition ${active ? "bg-brand-600 text-white" : "border border-border bg-white text-ink hover:bg-surface-muted"}`}
                    onClick={() => setSelected(method)}
                  >
                    {methodAsset.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="mt-5 rounded-xl border border-border bg-surface-muted/45 p-4">
            <p className="text-sm font-black text-ink">{asset.label}</p>
            <p className="mt-1 text-xs leading-5 text-ink-muted">{asset.accountLabel}</p>
            <p className="mt-3 text-xs leading-5 text-ink-muted">After payment, send the transaction reference or a screenshot so the payment can be confirmed in Ledgerly.</p>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <a
              href={asset.imageUrl}
              download={downloadName}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-ink hover:bg-surface-muted"
            >
              <Download className="h-4 w-4" />Download QR
            </a>
            <a
              href={asset.imageUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-ink-muted hover:bg-surface-muted hover:text-ink"
            >
              <ExternalLink className="h-4 w-4" />Open full size
            </a>
          </div>
        </div>

        <div className="flex items-center justify-center border-t border-border bg-surface-muted/45 p-5 lg:border-l lg:border-t-0">
          <img
            src={asset.imageUrl}
            alt={`${asset.label} QR code${asset.accountLabel ? ` for ${asset.accountLabel}` : ""}`}
            className="max-h-[520px] w-full max-w-[300px] rounded-2xl object-contain shadow-sm"
          />
        </div>
      </div>
    </Card>
  );
}
